import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { Campaign } from '@shared/types';
import { Spinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { IconCampaign, IconPlus } from '../components/Icons';
import { useToast } from '../lib/toast';
import { formatRelative } from '../lib/format';

interface ListResult {
  items: Campaign[];
  total: number;
}

export function CampaignsPage() {
  const [data, setData] = useState<ListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const toast = useToast();

  async function load() {
    try {
      const result = await api.get<ListResult>('/api/campaigns?pageSize=100');
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/api/campaigns', { name, description });
      toast.success('Campaign created');
      setName('');
      setDescription('');
      setShowCreate(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-sm text-ink-500 dark:text-ink-400">
            Group short links into campaigns and track performance together.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setShowCreate((v) => !v)}
        >
          <IconPlus />
          {showCreate ? 'Cancel' : 'New campaign'}
        </button>
      </header>

      {showCreate && (
        <form onSubmit={onCreate} className="card p-5 space-y-3">
          <div>
            <label className="label">Name</label>
            <input
              required
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              rows={2}
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="btn-primary" disabled={creating}>
              {creating && <Spinner size={16} />} Create
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {!data && (
        <div className="flex items-center justify-center py-16">
          <Spinner size={28} />
        </div>
      )}

      {data && data.items.length === 0 && (
        <EmptyState
          icon={<IconCampaign />}
          title="No campaigns yet"
          description="Group short links into campaigns to view aggregated analytics."
          action={
            <button className="btn-primary" type="button" onClick={() => setShowCreate(true)}>
              <IconPlus />
              Create campaign
            </button>
          }
        />
      )}

      {data && data.items.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.items.map((c) => (
            <Link
              key={c.id}
              to={`/app/campaigns/${c.id}`}
              className="card p-5 hover:border-brand-300 dark:hover:border-brand-700 transition"
            >
              <div className="font-semibold text-ink-900 dark:text-ink-50">{c.name}</div>
              {c.description && (
                <div className="text-sm text-ink-500 dark:text-ink-400 mt-1 line-clamp-2">
                  {c.description}
                </div>
              )}
              <div className="text-xs text-ink-400 dark:text-ink-500 mt-3">
                Created {formatRelative(c.createdAt)}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
